import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';
import {
  COUNTRY_OPTIONS,
  COUNTRY_OTHER,
  PostalCodeFormat,
  getPostalCodeFormat,
  isListedCountry,
} from '../profile-constants';
import type { User } from '../../../../shared/UserInterface';
import type { ProfileUpdateRequest } from '../../../../models/profile.interface';

/**
 * Form-local shape. `addressCountry` lives in two controls because the
 * dropdown can select a sentinel "Anderes Land" that reveals a manual
 * ISO-code input; the *effective* country saved to the backend is
 * resolved by {@link AddressInfoComponent.effectiveCountry}.
 */
interface AddressFormShape {
  addressStreet: string;
  addressHouseNumber: string;
  addressPostalCode: string;
  addressCity: string;
  countrySelection: string;
  countryManual: string;
}

@Component({
  selector: 'app-address-info',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent],
  templateUrl: './address-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../_collapsible-card.css'],
})
export class AddressInfoComponent {
  @Input({ required: true }) set user(value: User | null | undefined) {
    this._user = value;
    if (!value) return;
    // Don't clobber in-progress edits when the parent's currentUser signal
    // refreshes (e.g. after a sibling section saves). Collapsed cards always
    // re-sync; the saving card accepts the fresh payload via `isSaving()`.
    if (this.isExpanded() && this.form.dirty && !this.isSaving()) return;
    this.populateForm(value);
  }
  get user(): User | null | undefined {
    return this._user;
  }
  private _user: User | null | undefined;

  @Output() save = new EventEmitter<Partial<ProfileUpdateRequest>>();

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  readonly IconType = IconType;
  readonly countryOptions = COUNTRY_OPTIONS;
  readonly COUNTRY_OTHER = COUNTRY_OTHER;

  isExpanded = signal(false);
  isSaving = signal(false);

  /** Current postal-code constraints; depends on the effective country. */
  postalCodeFormat = signal<PostalCodeFormat>(getPostalCodeFormat(null));

  form: FormGroup = this.fb.group({
    addressStreet: ['', [Validators.maxLength(200)]],
    addressHouseNumber: ['', [Validators.maxLength(20)]],
    addressPostalCode: ['', [Validators.maxLength(20)]],
    addressCity: ['', [Validators.maxLength(200)]],
    countrySelection: [''],
    countryManual: ['', [Validators.pattern(/^[A-Za-z]{2}$/)]],
  });

  constructor() {
    // Re-derive postal-code validators whenever the effective country
    // changes. Subscribing to the FormGroup (not the child control) is
    // important: a child's `valueChanges` fires before the parent's
    // cached `value` is recomputed, so reading `form.value` from inside
    // a child subscription would see the *previous* selection.
    this.form.valueChanges
      .pipe(
        map(() => this.effectiveCountry()),
        startWith(this.effectiveCountry()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((country) => this.applyPostalCodeValidators(country));
  }

  /**
   * Resolve the single 2-letter country code from the dropdown + manual input.
   * Returns '' when nothing is selected, or when "Anderes Land" is selected
   * but the manual code isn't yet 2 alphabetic chars.
   */
  effectiveCountry(): string {
    const value = this.form.value as Partial<AddressFormShape>;
    const sel = value.countrySelection ?? '';
    if (sel === COUNTRY_OTHER) {
      const manual = (value.countryManual ?? '').trim().toUpperCase();
      return /^[A-Z]{2}$/.test(manual) ? manual : '';
    }
    return sel;
  }

  /** True when the dropdown is set to "Anderes Land" (reveals manual input). */
  showManualCountry(): boolean {
    return this.form.value.countrySelection === COUNTRY_OTHER;
  }

  private applyPostalCodeValidators(country: string): void {
    const format = getPostalCodeFormat(country);
    this.postalCodeFormat.set(format);
    const postal = this.form.get('addressPostalCode')!;
    postal.setValidators([
      Validators.maxLength(format.maxLength),
      Validators.pattern(format.pattern),
    ]);
    postal.updateValueAndValidity({ emitEvent: false });
  }

  toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
    if (this.isExpanded() && this._user) {
      this.populateForm(this._user);
    }
  }

  onSubmit(): void {
    if (!this.form.valid || this.isSaving()) return;
    const diff = this.collectDiff();
    if (Object.keys(diff).length === 0) {
      this.isExpanded.set(false);
      return;
    }
    this.isSaving.set(true);
    this.save.emit(diff);
  }

  onSaveComplete(success: boolean): void {
    this.isSaving.set(false);
    if (success) {
      this.isExpanded.set(false);
    }
  }

  hasError(field: keyof AddressFormShape, error: string): boolean {
    const control = this.form.get(field);
    return !!(control?.touched && control?.hasError(error));
  }

  /**
   * Strip non-digit characters as the user types or pastes. Keeps the DOM
   * input and the reactive form control in sync so the pattern validator
   * never has to fire for "letter typed" cases — letters simply can't end
   * up in the value.
   */
  onPostalCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/\D/g, '');
    if (cleaned !== input.value) {
      input.value = cleaned;
      this.form.get('addressPostalCode')!.setValue(cleaned);
    }
  }

  hasChanges(): boolean {
    return Object.keys(this.collectDiff()).length > 0;
  }

  /**
   * Country highlighting is special: both dropdown and manual input share
   * one persisted field (addressCountry). The selection input is "changed"
   * if the user's current pick maps to a different effective country than
   * what's stored; the manual input is "changed" when it's both visible and
   * differs from the stored value.
   */
  isFieldChanged(field: keyof AddressFormShape): boolean {
    const u = this._user;
    const value = this.form.value as AddressFormShape;
    if (field === 'countrySelection' || field === 'countryManual') {
      if (!u) return false;
      const stored = u.addressCountry ?? '';
      const storedSelection = isListedCountry(stored)
        ? stored
        : stored
          ? COUNTRY_OTHER
          : '';
      if (field === 'countrySelection') {
        return (value.countrySelection ?? '') !== storedSelection;
      }
      // countryManual: only flag when "Anderes Land" is selected and the
      // current manual code differs from the stored non-listed code.
      if (value.countrySelection !== COUNTRY_OTHER) return false;
      const storedManual = isListedCountry(stored) ? '' : stored;
      return (value.countryManual ?? '').toUpperCase() !== storedManual;
    }
    return field in this.collectDiff();
  }

  private populateForm(user: User): void {
    const stored = user.addressCountry ?? '';
    this.form.reset({
      addressStreet: user.addressStreet ?? '',
      addressHouseNumber: user.addressHouseNumber ?? '',
      addressPostalCode: user.addressPostalCode ?? '',
      addressCity: user.addressCity ?? '',
      countrySelection: stored
        ? isListedCountry(stored)
          ? stored
          : COUNTRY_OTHER
        : '',
      countryManual: isListedCountry(stored) ? '' : stored,
    });
  }

  private collectDiff(): Partial<ProfileUpdateRequest> {
    const value = this.form.value as AddressFormShape;
    const diff: Partial<ProfileUpdateRequest> = {};
    const u = this._user;
    if (!u) return diff;

    const stringKeys: (keyof Pick<
      AddressFormShape,
      'addressStreet' | 'addressHouseNumber' | 'addressPostalCode' | 'addressCity'
    >)[] = [
      'addressStreet',
      'addressHouseNumber',
      'addressPostalCode',
      'addressCity',
    ];

    for (const key of stringKeys) {
      const current = (u[key] ?? '') as string;
      if (value[key] !== current) {
        diff[key] = value[key];
      }
    }

    const effective = this.effectiveCountry();
    if (effective !== (u.addressCountry ?? '')) {
      diff.addressCountry = effective;
    }
    return diff;
  }
}
